import { useState, useEffect, useCallback } from 'react'
import api from '../../services/api'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../store/authStore'
import {
  QrCode, CheckCircle2, XCircle, Clock, RefreshCw,
  ShieldCheck, History, X,
} from 'lucide-react'
import LoadingSpinner from '../../components/LoadingSpinner'

const STATUS_CFG = {
  PENDING:   { label: 'Pending',   cls: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/25',   Icon: Clock },
  CONFIRMED: { label: 'Confirmed', cls: 'bg-green-500/10 text-green-400 border-green-500/25',     Icon: CheckCircle2 },
  REJECTED:  { label: 'Rejected',  cls: 'bg-red-500/10 text-red-400 border-red-500/25',           Icon: XCircle },
}

const REASON_LABELS = {
  FORGOT_QR: 'Forgot QR',
  REMOTE_CLASS: 'Remote Class',
  TECHNICAL_ISSUE: 'Technical Issue',
  OTHER: 'Other',
}

function fmtDateTime(s) {
  if (!s) return '—'
  return new Date(s).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function fmtDate(s) {
  if (!s) return '—'
  return new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

function getInitials(first, last) {
  return `${(first || '')[0] || ''}${(last || '')[0] || ''}`.toUpperCase()
}

/**
 * Unified Self-Reported Attendance management page for Admin and Trainer.
 * - Admin: sees pending submissions for their own taught courses (if they have
 *   a trainer record) OR all submissions (super-admin fallback).
 * - Trainer: sees pending submissions for their assigned courses only.
 *
 * Two tabs: "Pending Queue" (confirm/reject) and "QR Generator" (display
 * scannable QR for the current session).
 */
export default function SelfAttendanceQueue() {
  const { user } = useAuthStore()
  const isAdmin = user?.user_type === 'admin'

  const [tab, setTab] = useState('queue') // 'queue' | 'qr'
  const [courses, setCourses] = useState([])
  const [loadingCourses, setLoadingCourses] = useState(true)

  useEffect(() => {
    // Trainers fetch their own courses; admins fetch all (the backend scopes
    // the pending queue server-side regardless).
    const endpoint = isAdmin ? '/courses' : '/trainers/courses'
    api.get(endpoint)
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : data.courses || []
        setCourses(list)
      })
      .catch(() => toast.error('Failed to load courses'))
      .finally(() => setLoadingCourses(false))
  }, [isAdmin])

  return (
    <div className="space-y-8">
      <div className="page-header">
        <div>
          <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium mb-3 block">{isAdmin ? 'Admin' : 'Trainer'} Portal</span>
          <h1 className="text-2xl sm:text-3xl text-gray-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800 }}>
            Self-Reported Attendance
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-2 max-w-xl" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          Review student-submitted attendance (QR scans &amp; manual reports). Confirm or reject each request — every action is recorded in the audit trail.
        </p>
      </div>
      </div>

      <div className="flex gap-1 border-b border-gray-200 dark:border-gray-800">
        {[
          { id: 'queue', label: 'Pending Queue', Icon: Clock },
          { id: 'qr', label: 'QR Generator', Icon: QrCode },
        ].map(({ id, label, Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-all border-b-2 -mb-px ${
              tab === id
                ? 'text-primary-600 dark:text-primary-400 border-primary-500'
                : 'text-gray-500 dark:text-gray-400 border-transparent hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {tab === 'queue' && <PendingQueue courses={courses} loadingCourses={loadingCourses} />}
      {tab === 'qr' && <QrGenerator courses={courses} loadingCourses={loadingCourses} />}
    </div>
  )
}

// ─── Pending Queue ────────────────────────────────────────────────────────────

function PendingQueue({ courses, loadingCourses }) {
  const [submissions, setSubmissions] = useState([])
  const [loading, setLoading] = useState(true)
  const [filterCourse, setFilterCourse] = useState('')
  const [selected, setSelected] = useState(new Set())
  const [rejecting, setRejecting] = useState(null) // submission id being rejected
  const [rejectNote, setRejectNote] = useState('')
  const [auditFor, setAuditFor] = useState(null) // submission id whose audit is open
  const [auditRows, setAuditRows] = useState([])
  const [loadingAudit, setLoadingAudit] = useState(false)
  const [acting, setActing] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    const params = new URLSearchParams()
    if (filterCourse) params.append('course_id', filterCourse)
    api.get(`/attendance/self/pending?${params}`)
      .then(({ data }) => setSubmissions(data.submissions || []))
      .catch(() => toast.error('Failed to load pending submissions'))
      .finally(() => setLoading(false))
  }, [filterCourse])

  useEffect(() => { load() }, [load])
  useEffect(() => { setSelected(new Set()) }, [filterCourse, load])

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleAll = () => {
    if (selected.size === submissions.length) setSelected(new Set())
    else setSelected(new Set(submissions.map((s) => s.id)))
  }

  const confirmOne = async (id) => {
    setActing(true)
    try {
      await api.post('/attendance/self/confirm', { submission_id: id })
      toast.success('Attendance confirmed')
      setSubmissions((prev) => prev.filter((s) => s.id !== id))
      setSelected((prev) => { const n = new Set(prev); n.delete(id); return n })
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to confirm')
    } finally {
      setActing(false)
    }
  }

  const openReject = (id) => {
    setRejecting(id)
    setRejectNote('')
  }

  const submitReject = async () => {
    if (!rejecting) return
    setActing(true)
    try {
      await api.post('/attendance/self/reject', {
        submission_id: rejecting,
        note: rejectNote || null,
      })
      toast.success('Attendance rejected')
      setSubmissions((prev) => prev.filter((s) => s.id !== rejecting))
      setSelected((prev) => { const n = new Set(prev); n.delete(rejecting); return n })
      setRejecting(null)
      setRejectNote('')
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to reject')
    } finally {
      setActing(false)
    }
  }

  const bulkAction = async (action) => {
    if (selected.size === 0) return toast.error('Select at least one submission')
    setActing(true)
    try {
      const { data } = await api.post('/attendance/self/bulk', {
        submission_ids: Array.from(selected),
        action,
        note: null,
      })
      toast.success(data.message)
      setSubmissions((prev) => prev.filter((s) => !selected.has(s.id)))
      setSelected(new Set())
    } catch (error) {
      toast.error(error.response?.data?.error || 'Bulk action failed')
    } finally {
      setActing(false)
    }
  }

  const openAudit = async (id) => {
    setAuditFor(id)
    setLoadingAudit(true)
    setAuditRows([])
    try {
      const { data } = await api.get(`/attendance/self/audit/${id}`)
      setAuditRows(data.audit || [])
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load audit trail')
    } finally {
      setLoadingAudit(false)
    }
  }

  if (loading || loadingCourses) return <LoadingSpinner label="Loading pending submissions" />

  return (
    <div className="space-y-4">
      {/* Filters + bulk actions */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={filterCourse}
          onChange={(e) => setFilterCourse(e.target.value)}
          className="px-4 py-2.5 bg-transparent border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm focus:border-primary-500 focus:outline-none transition-colors min-w-[220px]"
        >
          <option value="">All my courses</option>
          {courses.map((c) => (
            <option key={c.id} value={c.id}>{c.title} ({c.course_code})</option>
          ))}
        </select>
        <button onClick={load} className="flex items-center gap-1.5 px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors text-sm">
          <RefreshCw size={14} /> Refresh
        </button>

        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {selected.size > 0 && `${selected.size} selected · `}
            {submissions.length} pending
          </span>
          {selected.size > 0 && (
            <>
              <button
                onClick={() => bulkAction('CONFIRM')}
                disabled={acting}
                className="px-3 py-1.5 text-xs font-sans font-medium bg-green-500/10 text-green-400 border border-green-500/25 hover:bg-green-500/20 transition-all flex items-center gap-1.5"
              >
                <CheckCircle2 size={13} /> Confirm Selected
              </button>
              <button
                onClick={() => bulkAction('REJECT')}
                disabled={acting}
                className="px-3 py-1.5 text-xs font-sans font-medium bg-red-500/10 text-red-400 border border-red-500/25 hover:bg-red-500/20 transition-all flex items-center gap-1.5"
              >
                <XCircle size={13} /> Reject Selected
              </button>
            </>
          )}
        </div>
      </div>

      {/* Table */}
      {submissions.length === 0 ? (
        <div className="text-center py-16">
          <CheckCircle2 size={36} className="mx-auto mb-4 text-gray-300 dark:text-gray-600" />
          <p className="text-sm text-gray-500 dark:text-gray-400">No pending attendance submissions</p>
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 dark:border-gray-800">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-800">
                <th className="text-left py-3 px-3 w-10">
                  <input
                    type="checkbox"
                    checked={selected.size === submissions.length && submissions.length > 0}
                    onChange={toggleAll}
                    className="w-4 h-4 accent-primary-500"
                  />
                </th>
                <th className="text-left py-3 px-3 text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium">Student</th>
                <th className="text-left py-3 px-3 text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium">Course</th>
                <th className="text-left py-3 px-3 text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium">Session Date</th>
                <th className="text-left py-3 px-3 text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium">Method</th>
                <th className="text-left py-3 px-3 text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium">Reason</th>
                <th className="text-left py-3 px-3 text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium">Submitted</th>
                <th className="text-right py-3 px-3 text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {submissions.map((s) => {
                const initials = getInitials(s.first_name, s.last_name)
                return (
                  <tr key={s.id} className="border-b border-gray-100 dark:border-white/5 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
                    <td className="py-3 px-3">
                      <input
                        type="checkbox"
                        checked={selected.has(s.id)}
                        onChange={() => toggleSelect(s.id)}
                        className="w-4 h-4 accent-primary-500"
                      />
                    </td>
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-primary-50 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400 flex items-center justify-center text-xs font-medium">
                          {initials}
                        </div>
                        <div>
                          <p className="text-sm text-gray-900 dark:text-white font-medium">
                            {s.first_name} {s.last_name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{s.student_number}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-3">
                      <p className="text-sm text-gray-900 dark:text-white">{s.course_title}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{s.course_code}</p>
                    </td>
                    <td className="py-3 px-3 text-xs text-gray-600 dark:text-gray-300">{fmtDate(s.session_date)}</td>
                    <td className="py-3 px-3">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium border rounded-sm ${
                        s.method === 'QR_SCAN'
                          ? 'bg-primary-50 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400 border-primary-200 dark:border-primary-500/30'
                          : 'bg-blue-500/10 text-blue-400 border-blue-500/25'
                      }`}>
                        {s.method === 'QR_SCAN' ? <QrCode size={11} /> : <ClipboardListIcon />}
                        {s.method === 'QR_SCAN' ? 'QR' : 'Manual'}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-xs text-gray-500 dark:text-gray-400">
                      {s.manual_reason ? (REASON_LABELS[s.manual_reason] || s.manual_reason) : '—'}
                    </td>
                    <td className="py-3 px-3 text-xs text-gray-500 dark:text-gray-400">{fmtDateTime(s.submitted_at)}</td>
                    <td className="py-3 px-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => confirmOne(s.id)}
                          disabled={acting}
                          title="Confirm"
                          className="p-1.5 bg-green-500/10 text-green-400 border border-green-500/25 hover:bg-green-500/20 transition-all"
                        >
                          <CheckCircle2 size={14} />
                        </button>
                        <button
                          onClick={() => openReject(s.id)}
                          disabled={acting}
                          title="Reject"
                          className="p-1.5 bg-red-500/10 text-red-400 border border-red-500/25 hover:bg-red-500/20 transition-all"
                        >
                          <XCircle size={14} />
                        </button>
                        <button
                          onClick={() => openAudit(s.id)}
                          title="Audit trail"
                          className="p-1.5 border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-all"
                        >
                          <History size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Reject modal */}
      {rejecting && (
        <Modal onClose={() => setRejecting(null)} title="Reject Attendance Submission">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Optionally add a note explaining why this submission is being rejected. The student will see this note in their submissions history.
          </p>
          <textarea
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder="e.g. Student not seen in class today"
            className="w-full px-4 py-3 bg-transparent border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:border-primary-500 focus:outline-none transition-colors resize-none mb-4"
          />
          <div className="flex gap-3 justify-end">
            <button onClick={() => setRejecting(null)} className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors text-sm">Cancel</button>
            <button
              onClick={submitReject}
              disabled={acting}
              className="px-4 py-2 text-sm font-medium bg-red-500/10 text-red-400 border border-red-500/25 hover:bg-red-500/20 flex items-center gap-2"
            >
              <XCircle size={15} /> Reject Submission
            </button>
          </div>
        </Modal>
      )}

      {/* Audit modal */}
      {auditFor && (
        <Modal onClose={() => setAuditFor(null)} title="Audit Trail" wide>
          {loadingAudit ? (
            <LoadingSpinner label="Loading audit trail" />
          ) : auditRows.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No audit entries found.</p>
          ) : (
            <ol className="space-y-3">
              {auditRows.map((row) => (
                <li key={row.id} className="border border-gray-200 dark:border-gray-700 p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium border rounded-sm ${
                      row.action_type === 'CONFIRM'
                        ? 'bg-green-500/10 text-green-400 border-green-500/25'
                        : row.action_type === 'REJECT'
                        ? 'bg-red-500/10 text-red-400 border-red-500/25'
                        : 'bg-primary-50 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400 border-primary-200 dark:border-primary-500/30'
                    }`}>
                      {row.action_type === 'CONFIRM' ? <CheckCircle2 size={11} /> : row.action_type === 'REJECT' ? <XCircle size={11} /> : <Clock size={11} />}
                      {row.action_type}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{fmtDateTime(row.created_at)}</span>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-300">
                    By: {row.first_name ? `${row.first_name} ${row.last_name}` : row.role || 'Student'} ·
                    {' '}{row.previous_status || '—'} → {row.new_status || '—'}
                  </p>
                  {row.notes && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Note: {row.notes}</p>}
                </li>
              ))}
            </ol>
          )}
        </Modal>
      )}
    </div>
  )
}

function ClipboardListIcon() {
  return <span className="text-[10px] font-bold">M</span>
}

// ─── QR Generator ─────────────────────────────────────────────────────────────

function QrGenerator({ courses, loadingCourses }) {
  const [courseId, setCourseId] = useState('')
  const [session, setSession] = useState(null)
  const [qrDataUrl, setQrDataUrl] = useState(null)
  const [generating, setGenerating] = useState(false)
  const [loadingActive, setLoadingActive] = useState(false)
  const [countdown, setCountdown] = useState(null)
  const [copied, setCopied] = useState(false)

  // Countdown timer for the active session.
  useEffect(() => {
    if (!session) return
    const tick = () => {
      const ms = new Date(session.valid_until) - new Date()
      if (ms <= 0) {
        setCountdown('Expired')
        return
      }
      const m = Math.floor(ms / 60000)
      const s = Math.floor((ms % 60000) / 1000)
      setCountdown(`${m}:${String(s).padStart(2, '0')}`)
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [session])

  const fetchActive = useCallback(async (id) => {
    if (!id) return
    setLoadingActive(true)
    try {
      const { data } = await api.get(`/attendance/self/active-qr?course_id=${id}`)
      if (data.session) {
        setSession(data.session)
        setQrDataUrl(data.qr_data_url)
      } else {
        setSession(null)
        setQrDataUrl(null)
      }
    } catch {
      // silent
    } finally {
      setLoadingActive(false)
    }
  }, [])

  useEffect(() => {
    if (courseId) fetchActive(courseId)
    else { setSession(null); setQrDataUrl(null) }
  }, [courseId, fetchActive])

  const generate = async () => {
    if (!courseId) return toast.error('Select a course')
    setGenerating(true)
    try {
      const { data } = await api.get(`/attendance/self/generate-qr?course_id=${courseId}`)
      setSession(data.session)
      setQrDataUrl(data.qr_data_url)
      toast.success('QR session generated — valid for 15 minutes')
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to generate QR')
    } finally {
      setGenerating(false)
    }
  }

  const copyToken = async () => {
    if (!session) return
    try {
      await navigator.clipboard.writeText(session.token)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      toast.error('Could not copy token')
    }
  }

  if (loadingCourses) return <LoadingSpinner label="Loading courses" />

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Controls */}
      <div className="space-y-5">
        <h2 className="text-lg font-display font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <QrCode size={18} className="text-primary-600 dark:text-primary-400" /> Generate Session QR
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Display this QR code at the start of class. Students scan it to submit their attendance. The token expires after 15 minutes.
        </p>

        <div>
          <label className="block text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium mb-2">Course</label>
          <select
            value={courseId}
            onChange={(e) => setCourseId(e.target.value)}
            className="w-full px-4 py-3 bg-transparent border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:border-primary-500 focus:outline-none transition-colors"
          >
            <option value="">Select a course…</option>
            {courses.map((c) => (
              <option key={c.id} value={c.id}>{c.title} ({c.course_code})</option>
            ))}
          </select>
        </div>

        <button
          onClick={generate}
          disabled={generating || !courseId}
          className="w-full px-4 py-3 text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
        >
          <QrCode size={16} /> {session ? 'Regenerate QR' : 'Generate QR'}
        </button>

        {session && (
          <div className="pt-4 border-t border-gray-200 dark:border-gray-800 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium">Status</span>
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-medium border rounded-sm ${
                countdown === 'Expired'
                  ? 'bg-red-500/10 text-red-400 border-red-500/25'
                  : 'bg-green-500/10 text-green-400 border-green-500/25'
              }`}>
                <Clock size={11} /> {countdown === 'Expired' ? 'Expired' : `Valid ${countdown}`}
              </span>
            </div>
            <div>
              <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium block mb-1">Token</span>
              <div className="flex items-center gap-2">
                <code className="flex-1 font-mono text-xs text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-900/50 px-3 py-2 truncate">
                  {session.token}
                </code>
                <button
                  onClick={copyToken}
                  className="px-3 py-2 border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white text-xs transition-colors"
                >
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <ShieldCheck size={13} className="text-primary-600 dark:text-primary-400" />
              Valid until {fmtDateTime(session.valid_until)}
            </div>
          </div>
        )}
      </div>

      {/* QR display */}
      <div className="flex flex-col items-center justify-center">
        {loadingActive ? (
          <LoadingSpinner label="Loading active QR" />
        ) : qrDataUrl ? (
          <>
            <div className="bg-white p-4 mb-4">
              <img src={qrDataUrl} alt="Attendance QR code" className="w-64 h-64" />
            </div>
            <p className="text-sm text-gray-900 dark:text-white font-medium mb-1">Scan to mark attendance</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {courses.find((c) => String(c.id) === String(courseId))?.title}
            </p>
            {countdown && countdown !== 'Expired' && (
              <p className="text-xs text-green-400 mt-2 flex items-center gap-1.5">
                <Clock size={12} /> Expires in {countdown}
              </p>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <QrCode size={48} className="mx-auto mb-4 text-gray-300 dark:text-gray-600" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {courseId ? 'No active QR session. Click "Generate QR".' : 'Select a course to begin.'}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Modal ────────────────────────────────────────────────────────────────────

function Modal({ children, onClose, title, wide = false }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={onClose}>
      <div
        className={`surface border border-gray-200 dark:border-gray-700 w-full ${wide ? 'max-w-2xl' : 'max-w-md'} max-h-[85vh] overflow-y-auto p-6`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-display font-semibold text-gray-900 dark:text-white">{title}</h3>
          <button onClick={onClose} className="text-gray-400 dark:text-gray-500 hover:text-gray-900 dark:hover:text-white">
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  )
}
